import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './Dock.module.css';
import { useCanvas } from '../../context/canvasContext';
import { useDocument } from '../../context/documentInfoContex';
import DockButton from './DockButton';
import { SelectIcon, PenIcon, TextIcon, ShapesIcon, EraserIcon, UploadIcon } from './icons';
import ShapesFlyout from './flyouts/ShapesFlyout';
import PenFlyout from './flyouts/PenFlyout';
import TextFlyout from './flyouts/TextFlyout';
import UploadModal from './UploadModal';
import UndoRedoButtons from './UndoRedoButtons';
import useEraser from './hooks/useEraser';
import usePenBrush, { DEFAULT_PEN } from './hooks/usePenBrush';
import useDeleteKey from './hooks/useDeleteKey';
import useTextSelection from './hooks/useTextSelection';

/**
 * The tool dock on the left of the board: one active tool at a time, with
 * the Pen, Text and Shapes options in a flyout beside it. Below 768px it
 * sits along the bottom and carries undo / redo too.
 */
const Dock = () => {
    const { id } = useParams();
    const { canvasRef, createIText, saveStateToUndoStack } = useCanvas();
    const { isReadOnly } = useDocument();

    const [activeTool, setActiveTool] = useState('select');
    const [flyout, setFlyout] = useState(null); // 'pen' | 'text' | 'shapes' | null
    const [uploadOpen, setUploadOpen] = useState(false);

    const [textColor, setTextColor] = useState();
    const [textSize, setTextSize] = useState();
    const [textFont, setTextFont] = useState();
    const [textAlign, setTextAlign] = useState();
    const [textStyle, setTextStyle] = useState();
    const textSetters = useMemo(
        () => ({ setTextColor, setTextSize, setTextFont, setTextAlign, setTextStyle }),
        []
    );

    const eraser = useEraser(canvasRef, saveStateToUndoStack);
    const pen = usePenBrush(canvasRef);
    useDeleteKey(canvasRef, saveStateToUndoStack);

    const selectTool = useCallback(() => {
        eraser.stop();
        canvasRef.current.isDrawingMode = false;
        setFlyout(null);
        setActiveTool('select');
    }, [eraser, canvasRef]);

    const closeTextFlyout = useCallback(() => {
        setFlyout((current) => (current === 'text' ? null : current));
        setActiveTool((current) => (current === 'text' ? 'select' : current));
    }, []);
    useTextSelection(canvasRef, textSetters, closeTextFlyout);

    const penTool = () => {
        eraser.stop();
        canvasRef.current.isDrawingMode = true;
        pen.apply(DEFAULT_PEN);
        setFlyout('pen');
        setActiveTool('pen');
    };

    const textTool = () => {
        eraser.stop();
        canvasRef.current.isDrawingMode = false;
        const active = canvasRef.current.getActiveObject();
        if (!(active && active.isType('i-text'))) createIText(id, textSetters);
        const opening = flyout !== 'text';
        setFlyout(opening ? 'text' : null);
        setActiveTool(opening ? 'text' : 'select');
    };

    const shapesTool = () => {
        eraser.stop();
        const opening = flyout !== 'shapes';
        setFlyout(opening ? 'shapes' : null);
        setActiveTool(opening ? 'shapes' : 'select');
    };

    const eraserTool = () => {
        eraser.start();
        setFlyout(null);
        setActiveTool('eraser');
    };

    const uploadTool = () => {
        eraser.stop();
        setFlyout(null);
        setUploadOpen(true);
    };

    if (isReadOnly) return null;

    return (
        <>
            <div className={styles.dock}>
                <DockButton title="Select" active={activeTool === 'select'} onClick={selectTool}><SelectIcon /></DockButton>
                <DockButton title="Pen" active={activeTool === 'pen'} onClick={penTool}><PenIcon /></DockButton>
                <DockButton title="Text" active={activeTool === 'text'} onClick={textTool}><TextIcon /></DockButton>
                <DockButton title="Shapes" active={activeTool === 'shapes'} onClick={shapesTool}><ShapesIcon /></DockButton>
                <span className={styles.divider} aria-hidden="true" />
                <DockButton title="Eraser" active={activeTool === 'eraser'} onClick={eraserTool}><EraserIcon /></DockButton>
                <DockButton title="Upload image" onClick={uploadTool}><UploadIcon /></DockButton>
                <div className={styles.history}>
                    <span className={styles.divider} aria-hidden="true" />
                    <UndoRedoButtons />
                </div>
            </div>

            {flyout === 'shapes' && <ShapesFlyout onClose={selectTool} />}
            {flyout === 'pen' && <PenFlyout options={pen.options} apply={pen.apply} onClose={selectTool} />}
            {flyout === 'text' && (
                <TextFlyout
                    text={{ textColor, textSize, textFont, textAlign, textStyle, ...textSetters }}
                    onClose={closeTextFlyout}
                />
            )}
            <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
        </>
    );
};

export default Dock;
